import { connect } from "react-redux";

import { withResolvePaths } from "../../../withResolvePaths";
import { withFocusManagement } from "../../../Services/FocusManagement/withFocusManagement";
import { withUnitRegistry } from "../../Navigation/UnitRegistry/UnitRegistryContext";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { CandyDispatch } from "../../../../Engine/Core/Store/CandyReduxTypes";
import { addAttachmentPublicFormAction } from "../../../../Engine/ActionCreators/AttachmentActionCreators";
import { IPublicForm } from "../../../../Forms/PublicForm/IPublicForm";
import { AttachmentInfoPath, UnitItemExtraInfoPath } from "../../../../../../Common/PathConstants";
import { withEvaluators } from "../../../withEvaluators";
import { withVisibilityProps } from "../../../withVisibilityProps";
import { withResolveUnitId } from "../../../withResolveUnitId";
import { withViewModelSelect } from "../../../withViewModelSelect";
import { NavigationSettings } from "../../AttachmentForm/AttachmentFormRenderer";
import {
    switchOffErrorsLogTooltip,
    switchOnErrorsLogTooltipUseHighlanderPath,
} from "../../../../Engine/ActionCreators/ErrorLogTooltipActionCreators";
import { setToModel } from "../../../../Engine/ActionCreators/AllActionCreators";
import { CurrencyHelper } from "../../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyHelper";

import { AttachmentFormLayout, AttachmentFormLayoutProps } from "./AttachmentFormLayout";

export type AttachmentFormLayoutContainerProps = Omit<
    AttachmentFormLayoutProps,
    | "onRenderAttachmentForm"
    | "navigationSettings"
    | "attachmentInfo"
    | "isFormInFormNds"
    | "switchOnErrorsLogTooltip"
    | "switchOffErrorsLogTooltip"
    | "setUnitItemExtraInfo"
>;

const mapStateToProps = (state: FullFormState, ownProps: AttachmentFormLayoutContainerProps) => {
    const bindingPath = ownProps.bindingPath.joinWith(AttachmentInfoPath);
    const attachmentId = bindingPath.isResolved()
        ? ownProps.viewModelSelect.controlModel(state, bindingPath)?.value
        : null;

    const navigationSettings: NavigationSettings = {};
    const navigationAttachmentId =
        state.errorLog.attachmentErrorLogs?.attachmentId || state.settings.navigationAttachmentId;

    if (navigationAttachmentId === attachmentId) {
        navigationSettings.navigationAttachmentId = navigationAttachmentId;

        const navigationPath = state.settings.navigationPath;
        const navigationHighlanderPath = state.errorLog.attachmentErrorLogs?.path;
        navigationSettings.errorItemId = state.errorLog.attachmentErrorLogs?.errorItemId;
        navigationSettings.navigationHighlanderPath = navigationHighlanderPath;
        navigationSettings.navigationPath = navigationPath;
        navigationSettings.navigationQueryPath = state.settings.navigationQueryPath;
    }

    return {
        attachmentInfo: attachmentId ? state.attachments.byId[attachmentId] : undefined,
        visible: state.settings.isReadOnly ? false : ownProps.processEvaluableProp(state, ownProps.visible),
        navigationSettings: navigationSettings,
        isFormInFormNds: !!state.settings.isFormInFormNds,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: AttachmentFormLayoutContainerProps) => ({
    onRenderAttachmentForm: (id: string, publicForm: IPublicForm) =>
        dispatch(addAttachmentPublicFormAction(id, publicForm)),
    switchOnErrorsLogTooltip: (highlanderPath: string, errorId: string, attachmentId: string) =>
        dispatch(switchOnErrorsLogTooltipUseHighlanderPath(highlanderPath, errorId, attachmentId)),
    switchOffErrorsLogTooltip: () => dispatch(switchOffErrorsLogTooltip()),
    setUnitItemExtraInfo: (infos?: Array<string | undefined> | string) => {
        if (!ownProps.bindingPath.isResolved()) {
            return;
        }

        const unitItemExtraInfoPath = ownProps.bindingPath.joinWith(UnitItemExtraInfoPath);

        if (infos === undefined) {
            dispatch(setToModel(unitItemExtraInfoPath, "value", undefined));
        }

        let formattedSum = Array.isArray(infos)
            ? infos.reduce((acc, item) => acc + (Number(item) || 0), 0).toFixed(2)
            : infos;

        formattedSum = CurrencyHelper.format(formattedSum, { fractionDigits: 2 });
        formattedSum = formattedSum ? `${formattedSum} ₽` : formattedSum;

        dispatch(setToModel(unitItemExtraInfoPath, "value", formattedSum));
    },
});

export const AttachmentFormLayoutContainer = withResolvePaths(
    withResolveUnitId(
        withViewModelSelect(
            withFocusManagement(
                withEvaluators(
                    withUnitRegistry(
                        connect(mapStateToProps, mapDispatchToProps)(withVisibilityProps(AttachmentFormLayout))
                    )
                )
            )
        )
    )
);
